import { makeStyles, tokens } from '@fluentui/react-components';
import React, { useCallback, useMemo } from 'react';
import ReactMarkdown, { Components, ExtraProps, Options as ReactMarkdownOptions } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema, Options as SanitizeSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { AuthorRoles, IChatMessage } from '../../../libs/models/ChatMessage';
import { rehypeRestrictImageHosts } from '../../../libs/utils/answerImageHosts';
import { rehypeRestrictLinkHosts, sourceHostsOf } from '../../../libs/utils/answerLinkHosts';
import { HastNode } from '../../../libs/utils/answerUrl';
import { containsLatex, AiaLatex } from '../../utils/AiaLatex';
import { formatChatMessage } from '../../utils/ChatMessageUtils';
import CustomLink from '../../utils/CustomLink';
import { ChatHistorySourceLinks } from './ChatHistorySourceLinks';
import { SourceChip } from './SourceChip';
import { SupportMessageText } from './SupportMessageText';

interface ChatHistoryTextContentProps {
    message: IChatMessage;
    isLastMessage: boolean;
    isChatResponseComplete: boolean;
    isUser: boolean;
    dynamicReport: boolean;
    useSourcesDropdown: boolean;
}

// Tame markdown headings: the model emits "#" section titles (e.g. "# Antaganden"),
// which would otherwise render at the browser default h1/h2 size. Match the agent
// console (agent-workspace.css) — all levels render as bold text at body size.
// Each heading level is defined individually: Griffel's atomic CSS compiler does not
// reliably support comma-separated nested selectors.
const headingStyle = {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase300,
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalXS,
};

const useClasses = makeStyles({
    content: {
        '& *:first-child': {
            marginTop: '0',
        },
        '& *:last-child': {
            marginBottom: '0',
        },
        '& h1': headingStyle,
        '& h2': headingStyle,
        '& h3': headingStyle,
        '& h4': headingStyle,
        '& h5': headingStyle,
        '& h6': headingStyle,
    },
    sourceLinks: {
        marginTop: tokens.spacingVerticalM,
    },
});

// rehype-raw is needed because answers carry HTML the app relies on: formatChatMessage injects the
// citation <sup> markers, and backend templates and executors send raw <a href target="_blank"> links
// (the schema drops `target`; CustomLink sets it again). The rest of a bot answer is untrusted: prompt
// injection via indexed content or tool data can steer it into emitting markup, and the CSP stops
// scripts, not injected elements or inline CSS (#2561). So sanitize right after rehype-raw with GitHub's
// schema, widened only for what the app emits itself.
const droppedAttributes = new Set([
    'accessKey',
    'tabIndex',
    'width',
    'height',
    'hSpace',
    'border',
    'cellPadding',
    'cellSpacing',
    'size',
]);

const sanitizeSchema: SanitizeSchema = {
    // The schema prefixes every id and name with `user-content-` against DOM clobbering. remark-rehype has
    // already given footnote ids that prefix, so theirs get it twice and no longer match their links.
    // Accepted: answers cite with [[#N]], not footnotes, and CustomLink opens every link in a new tab, so a
    // footnote link never jumped within the answer anyway.
    ...defaultSchema,
    // <source srcset> loads an image on render, and no protocol check covers srcset the way one covers
    // src. Markdown can't produce either tag. A missing list would allow every tag, hence the `[]`.
    tagNames: (defaultSchema.tagNames ?? []).filter((tagName) => tagName !== 'picture' && tagName !== 'source'),
    // Disallowed tags are unwrapped by default, which would put the raw text these hold (CSS, markup, the
    // rest of an unclosed answer) on screen.
    strip: [
        ...(defaultSchema.strip ?? []),
        'style',
        'textarea',
        'title',
        'xmp',
        'noembed',
        'noframes',
        'iframe',
        'plaintext',
    ],
    attributes: {
        ...defaultSchema.attributes,
        // Markdown never sets these. From an answer, a huge width/height/border/size stretches the bubble,
        // and accessKey/tabIndex let a key press land on an injected link.
        '*': (defaultSchema.attributes?.['*'] ?? []).filter(
            (attribute) => !droppedAttributes.has(typeof attribute === 'string' ? attribute : attribute[0]),
        ),
        sup: [...(defaultSchema.attributes?.sup ?? []), 'dataRefIds'],
    },
};

const urlProperties = Object.keys(defaultSchema.protocols ?? {});

// hast-util-sanitize matches URL schemes case-sensitively, unlike browsers and react-markdown's own check,
// so it would drop the href of `Https://…` or `MAILTO:…`. The lowercased scheme still has to be on the
// allowlist, so `JAVASCRIPT:` is dropped as before.
const lowercaseUrlSchemes = (node: HastNode): void => {
    const { properties } = node;
    if (properties) {
        for (const key of urlProperties) {
            const value = properties[key];
            if (typeof value === 'string') {
                properties[key] = value.replace(/^[a-z][a-z\d+.-]*(?=:)/i, (scheme) => scheme.toLowerCase());
            }
        }
    }
    node.children?.forEach(lowercaseUrlSchemes);
};

const rehypeLowercaseUrlSchemes = () => lowercaseUrlSchemes;

// A bot answer's links are held to the same hosts as its images, plus the hosts of its own cited
// sources (#2856), so the plugin list is built per message rather than once for the module.
const buildRehypePlugins = (sourceHosts: readonly string[]): ReactMarkdownOptions['rehypePlugins'] => [
    rehypeRaw,
    rehypeLowercaseUrlSchemes,
    [rehypeSanitize, sanitizeSchema],
    rehypeRestrictImageHosts,
    [rehypeRestrictLinkHosts, { sourceHosts }],
];

// react-markdown hands its hast `node` to overrides; spread onto a DOM element it renders as
// node="[object Object]".
const LatexParagraph = ({ node: _node, ...props }: React.JSX.IntrinsicElements['p'] & ExtraProps) =>
    typeof props.children === 'string' && containsLatex(props.children) ? (
        <AiaLatex content={props.children} />
    ) : (
        <p {...props} />
    );

const LatexListItem = ({ node: _node, ...props }: React.JSX.IntrinsicElements['li'] & ExtraProps) =>
    typeof props.children === 'string' && containsLatex(props.children) ? (
        <li>
            <AiaLatex content={props.children} />
        </li>
    ) : (
        <li {...props} />
    );

export const ChatHistoryTextContent: React.FC<ChatHistoryTextContentProps> = React.memo(
    ({ message, isLastMessage, isChatResponseComplete, isUser, dynamicReport, useSourcesDropdown }) => {
        const content = useMemo(() => formatChatMessage(message, useSourcesDropdown), [message, useSourcesDropdown]);
        const classes = useClasses();
        const hasLatex = useMemo(() => containsLatex(content), [content]);

        const contentMetadata = message.contentMetadataArray?.contentMetadata;

        const rehypePlugins = useMemo(
            () => buildRehypePlugins(sourceHostsOf((contentMetadata ?? []).map((source) => source.url))),
            [contentMetadata],
        );

        const showLegacyLinks = useMemo(() => {
            return (
                !useSourcesDropdown &&
                (isChatResponseComplete || !isLastMessage) &&
                contentMetadata?.filter((x) => x.orderOfAppearance > 0).pop()
            );
        }, [useSourcesDropdown, isChatResponseComplete, isLastMessage, contentMetadata]);

        const supComponent = useCallback(
            (props: React.HTMLAttributes<HTMLElement> & { 'data-ref-ids'?: string; node?: unknown }) => {
                // Strip react-markdown's `node` prop so it isn't spread onto the DOM <sup> element
                // (otherwise it renders as <sup node="[object Object]"> and React warns).
                const { node: _node, ...rest } = props;
                const refIdsAttr = rest['data-ref-ids'];
                if (useSourcesDropdown && refIdsAttr && contentMetadata) {
                    return <SourceChip refIds={refIdsAttr} contentMetadata={contentMetadata} />;
                }
                return <sup {...rest} />;
            },
            [useSourcesDropdown, contentMetadata],
        );

        const components = useMemo<Components>(
            () => ({
                a: ({ node: _node, ...props }) => (
                    <CustomLink {...props} message={message} isLastMessage={isLastMessage} />
                ),
                sup: supComponent,
                ...(hasLatex ? { p: LatexParagraph, li: LatexListItem } : {}),
            }),
            [message, isLastMessage, supComponent, hasLatex],
        );

        const renderContent = useCallback(() => {
            if (isUser) {
                // Only a support agent's URLs become links: the customer's own text stays inert.
                return (
                    <div>
                        {message.authorRole === AuthorRoles.Participant ? (
                            <SupportMessageText text={content} />
                        ) : (
                            content
                        )}
                    </div>
                );
            }

            return (
                <ReactMarkdown
                    rehypePlugins={rehypePlugins}
                    remarkPlugins={[remarkGfm]}
                    className={classes.content}
                    components={components}
                >
                    {content}
                </ReactMarkdown>
            );
        }, [isUser, message.authorRole, content, classes.content, components, rehypePlugins]);

        return (
            <>
                {renderContent()}
                {message.graphData && dynamicReport && (
                    <div style={{ maxHeight: '600px' }}>{/* Render graph data here */}</div>
                )}
                {showLegacyLinks && (
                    <div className={classes.sourceLinks}>
                        <ChatHistorySourceLinks contentMetadata={contentMetadata} />
                    </div>
                )}
            </>
        );
    },
);

ChatHistoryTextContent.displayName = 'ChatHistoryTextContent';

export default ChatHistoryTextContent;
